import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Calculator, ChevronRight, MessageCircle, IndianRupee, Percent, Clock, CreditCard, Building, Car, Home, Briefcase, TrendingUp, HelpCircle } from "lucide-react";
import useJsonLd from "../hooks/useJsonLd";
import useCanonical from "../hooks/useCanonical";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const WHATSAPP_URL = "https://wa.me/918940988776?text=Hi%2C%20I%20need%20loan%20assistance%20in%20Coimbatore";

const LOAN_PRESETS = [
  { label: "Personal Loan", icon: <CreditCard size={18} />, rate: 14, min: 10, max: 18, tenure: 5 },
  { label: "Business Loan", icon: <Briefcase size={18} />, rate: 13, min: 9, max: 18, tenure: 5 },
  { label: "MSME Loan", icon: <Building size={18} />, rate: 12, min: 9, max: 17, tenure: 5 },
  { label: "Working Capital", icon: <TrendingUp size={18} />, rate: 14, min: 9, max: 20, tenure: 3 },
  { label: "Home Loan", icon: <Home size={18} />, rate: 9, min: 8.5, max: 10.5, tenure: 20 },
  { label: "Vehicle Loan", icon: <Car size={18} />, rate: 11, min: 8, max: 14, tenure: 5 },
];

const faqs = [
  { q: "How is EMI calculated?", a: "EMI is calculated using the formula: EMI = [P x R x (1+R)^N] / [(1+R)^N - 1], where P = Loan Amount, R = Monthly Interest Rate (annual rate / 12 / 100), and N = Number of Monthly Installments. Our calculator does this automatically for you." },
  { q: "Can I reduce my EMI?", a: "Yes, you can reduce EMI by: (1) Choosing a longer tenure, (2) Making a larger down payment, (3) Negotiating a lower interest rate, (4) Improving your CIBIL score before applying. However, longer tenure means more total interest paid." },
  { q: "Does prepayment reduce EMI?", a: "Prepayment reduces either your EMI amount or your loan tenure, depending on your lender's policy. Most banks allow partial prepayment without penalty after 6-12 months. Home loans with floating rates have zero prepayment charges by RBI regulation." },
  { q: "What happens if I miss EMI?", a: "Missing EMI payments leads to: late payment charges (1-2% of EMI), negative impact on CIBIL score, potential loan recall after 3+ missed payments, and legal action in severe cases. Always inform your lender in advance if you anticipate difficulty." },
  { q: "Is EMI different for NBFC vs Bank?", a: "Yes, NBFCs typically charge slightly higher interest rates than banks (1-3% more), resulting in higher EMI. However, NBFCs often have faster approval, more flexible eligibility, and may approve applicants that banks reject." },
  { q: "How much EMI can I afford based on income?", a: "As a rule of thumb, your total EMI obligations (all loans combined) should not exceed 40-50% of your net monthly income. For example, if your salary is Rs. 50,000, your total EMIs should ideally stay below Rs. 20,000-25,000." },
];

const formatCurrency = (num) => {
  if (num >= 10000000) return `${(num / 10000000).toFixed(2)} Cr`;
  if (num >= 100000) return `${(num / 100000).toFixed(2)} L`;
  return num.toLocaleString("en-IN", { maximumFractionDigits: 0 });
};

const EMICalculatorPage = () => {
  const [loanAmount, setLoanAmount] = useState(1000000);
  const [interestRate, setInterestRate] = useState(12);
  const [tenure, setTenure] = useState(5);
  const [tenureType, setTenureType] = useState("years");
  const [activePreset, setActivePreset] = useState(null);
  const [showSchedule, setShowSchedule] = useState(false);

  useCanonical("/emi-calculator");
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "WINTRUST SOLUTIONS",
    "description": "Free Loan EMI Calculator for personal, business, home, MSME and vehicle loans. Expert loan assistance in Coimbatore.",
    "url": "https://wintrustsolutions.com/emi-calculator",
    "telephone": "+918940988776",
    "address": { "@type": "PostalAddress", "addressLocality": "Coimbatore", "addressRegion": "Tamil Nadu", "addressCountry": "IN" },
  });

  const results = useMemo(() => {
    const P = loanAmount;
    const annualRate = interestRate;
    const R = annualRate / 12 / 100;
    const N = tenureType === "years" ? tenure * 12 : tenure;

    if (P <= 0 || R <= 0 || N <= 0) return { emi: 0, totalInterest: 0, totalAmount: 0, schedule: [] };

    const emi = (P * R * Math.pow(1 + R, N)) / (Math.pow(1 + R, N) - 1);
    const totalAmount = emi * N;
    const totalInterest = totalAmount - P;

    const schedule = [];
    let balance = P;
    for (let m = 1; m <= N; m++) {
      const interest = balance * R;
      const principal = emi - interest;
      balance = Math.max(0, balance - principal);
      schedule.push({ month: m, emi, principal, interest, balance });
    }

    return { emi, totalInterest, totalAmount, schedule };
  }, [loanAmount, interestRate, tenure, tenureType]);

  const selectPreset = (preset, idx) => {
    setActivePreset(idx);
    setInterestRate(preset.rate);
    setTenure(preset.tenure);
    setTenureType("years");
  };

  const interestPercent = results.totalAmount > 0 ? (results.totalInterest / results.totalAmount) * 100 : 0;
  const principalPercent = 100 - interestPercent;

  return (
    <>
      <Helmet>
        <title>Loan EMI Calculator – All Loan Types | WinTrust</title>
        <meta name="description" content="Calculate EMI for personal, business, home, MSME and vehicle loans instantly. Free online EMI calculator with amortization schedule." />
      </Helmet>

      {/* Hero */}
      <section className="bg-navy py-12 md:py-16">
        <div className="container-custom">
          <nav className="text-slate-400 text-sm mb-4">
            <Link to="/" className="hover:text-gold">Home</Link> / <span className="text-white">EMI Calculator</span>
          </nav>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-heading leading-tight mb-4" data-testid="emi-h1">
            Loan EMI Calculator – Calculate EMI for All Loan Types
          </h1>
          <p className="text-slate-300 text-lg max-w-3xl">
            EMI (Equated Monthly Instalment) is the fixed monthly payment you make to repay a loan. Calculating your EMI before applying helps you plan your budget, compare loan offers, and choose the best tenure. Use our EMI calculator — ideal for borrowers in Coimbatore & Tamil Nadu.
          </p>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="bg-slate-50 section-padding" data-testid="emi-calculator-section">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-8 font-heading text-center">Universal EMI Calculator</h2>

          {/* Loan Type Presets */}
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {LOAN_PRESETS.map((preset, idx) => (
              <button
                key={idx}
                onClick={() => selectPreset(preset, idx)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border transition-all ${activePreset === idx ? "bg-navy text-white border-navy" : "bg-white text-navy border-slate-200 hover:border-gold"}`}
                data-testid={`preset-${idx}`}
              >
                {preset.icon}
                {preset.label}
                <span className="text-xs opacity-60">({preset.min}%-{preset.max}%)</span>
              </button>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-10">
            {/* Input Panel */}
            <div className="bg-white rounded-xl p-6 md:p-8 border border-slate-200 shadow-sm" data-testid="emi-input-panel">
              {/* Loan Amount */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-3">
                  <label className="text-navy font-semibold flex items-center gap-2"><IndianRupee size={16} /> Loan Amount</label>
                  <div className="flex items-center gap-1 bg-slate-50 rounded-lg px-3 py-1.5 border border-slate-200">
                    <span className="text-navy font-semibold text-sm">₹</span>
                    <input
                      type="number"
                      value={loanAmount}
                      onChange={(e) => setLoanAmount(Math.max(0, Number(e.target.value)))}
                      className="w-28 bg-transparent text-navy font-semibold text-sm text-right focus:outline-none"
                      data-testid="loan-amount-input"
                    />
                  </div>
                </div>
                <input
                  type="range"
                  min={50000}
                  max={50000000}
                  step={50000}
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(Number(e.target.value))}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-gold"
                  data-testid="loan-amount-slider"
                />
                <div className="flex justify-between text-xs text-slate-400 mt-1"><span>₹50K</span><span>₹5 Cr</span></div>
              </div>

              {/* Interest Rate */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-3">
                  <label className="text-navy font-semibold flex items-center gap-2"><Percent size={16} /> Interest Rate (p.a.)</label>
                  <div className="flex items-center gap-1 bg-slate-50 rounded-lg px-3 py-1.5 border border-slate-200">
                    <input
                      type="number"
                      value={interestRate}
                      onChange={(e) => setInterestRate(Math.max(0, Math.min(50, Number(e.target.value))))}
                      step={0.1}
                      className="w-16 bg-transparent text-navy font-semibold text-sm text-right focus:outline-none"
                      data-testid="interest-rate-input"
                    />
                    <span className="text-navy font-semibold text-sm">%</span>
                  </div>
                </div>
                <input
                  type="range"
                  min={1}
                  max={36}
                  step={0.5}
                  value={interestRate}
                  onChange={(e) => setInterestRate(Number(e.target.value))}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-gold"
                  data-testid="interest-rate-slider"
                />
                <div className="flex justify-between text-xs text-slate-400 mt-1"><span>1%</span><span>36%</span></div>
              </div>

              {/* Tenure */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="text-navy font-semibold flex items-center gap-2"><Clock size={16} /> Loan Tenure</label>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 bg-slate-50 rounded-lg px-3 py-1.5 border border-slate-200">
                      <input
                        type="number"
                        value={tenure}
                        onChange={(e) => setTenure(Math.max(1, Number(e.target.value)))}
                        className="w-12 bg-transparent text-navy font-semibold text-sm text-right focus:outline-none"
                        data-testid="tenure-input"
                      />
                    </div>
                    <select
                      value={tenureType}
                      onChange={(e) => setTenureType(e.target.value)}
                      className="bg-slate-50 rounded-lg px-3 py-1.5 border border-slate-200 text-navy text-sm font-semibold focus:outline-none"
                      data-testid="tenure-type-select"
                    >
                      <option value="years">Years</option>
                      <option value="months">Months</option>
                    </select>
                  </div>
                </div>
                <input
                  type="range"
                  min={1}
                  max={tenureType === "years" ? 30 : 360}
                  step={1}
                  value={tenure}
                  onChange={(e) => setTenure(Number(e.target.value))}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-gold"
                  data-testid="tenure-slider"
                />
                <div className="flex justify-between text-xs text-slate-400 mt-1">
                  <span>1</span><span>{tenureType === "years" ? "30 Yrs" : "360 Mo"}</span>
                </div>
              </div>
            </div>

            {/* Results Panel */}
            <div className="space-y-6" data-testid="emi-results-panel">
              {/* Main EMI Result */}
              <div className="bg-navy rounded-xl p-6 md:p-8 text-center">
                <p className="text-slate-400 text-sm mb-1">Monthly EMI</p>
                <p className="text-4xl md:text-5xl font-bold text-gold font-heading" data-testid="emi-result">
                  ₹{results.emi > 0 ? formatCurrency(Math.round(results.emi)) : "0"}
                </p>
              </div>

              {/* Breakdown */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-xl p-5 border border-slate-200 text-center">
                  <p className="text-slate-500 text-xs mb-1">Total Interest</p>
                  <p className="text-xl font-bold text-navy" data-testid="total-interest">₹{formatCurrency(Math.round(results.totalInterest))}</p>
                </div>
                <div className="bg-white rounded-xl p-5 border border-slate-200 text-center">
                  <p className="text-slate-500 text-xs mb-1">Total Amount</p>
                  <p className="text-xl font-bold text-navy" data-testid="total-amount">₹{formatCurrency(Math.round(results.totalAmount))}</p>
                </div>
              </div>

              {/* Get This Loan CTA */}
              <a
                href="https://wa.me/918940988776?text=Hi%2C%20I%20calculated%20my%20EMI%20and%20want%20to%20apply%20for%20a%20loan"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold/90 text-navy font-semibold px-6 py-4 rounded-xl shadow-md transition-colors text-base sm:text-lg"
                data-testid="get-this-loan-btn"
              >
                <MessageCircle size={20} /> Get This Loan — WhatsApp Us Now
              </a>

              {/* Visual Bar */}
              <div className="bg-white rounded-xl p-5 border border-slate-200">
                <div className="flex justify-between text-xs mb-2">
                  <span className="text-navy font-medium">Principal: {principalPercent.toFixed(1)}%</span>
                  <span className="text-gold font-medium">Interest: {interestPercent.toFixed(1)}%</span>
                </div>
                <div className="h-4 rounded-full bg-slate-100 overflow-hidden flex">
                  <div className="bg-navy rounded-l-full transition-all" style={{ width: `${principalPercent}%` }} />
                  <div className="bg-gold rounded-r-full transition-all" style={{ width: `${interestPercent}%` }} />
                </div>
              </div>

              {/* Toggle Schedule */}
              <button
                onClick={() => setShowSchedule(!showSchedule)}
                className="w-full py-3 bg-white border border-slate-200 rounded-xl text-navy font-medium hover:border-gold transition-colors text-sm"
                data-testid="toggle-schedule-btn"
              >
                {showSchedule ? "Hide" : "Show"} Amortization Schedule
              </button>
            </div>
          </div>

          {/* Amortization Schedule */}
          {showSchedule && results.schedule.length > 0 && (
            <div className="mt-10 bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto" data-testid="amortization-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-navy text-white">
                    <th className="p-3 text-left font-medium">Month</th>
                    <th className="p-3 text-right font-medium">EMI (₹)</th>
                    <th className="p-3 text-right font-medium">Principal (₹)</th>
                    <th className="p-3 text-right font-medium">Interest (₹)</th>
                    <th className="p-3 text-right font-medium">Balance (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {results.schedule.map((row) => (
                    <tr key={row.month} className={`border-b border-slate-100 ${row.month % 2 === 0 ? "bg-slate-50" : ""}`}>
                      <td className="p-3 text-navy font-medium">{row.month}</td>
                      <td className="p-3 text-right text-slate-600">{Math.round(row.emi).toLocaleString("en-IN")}</td>
                      <td className="p-3 text-right text-navy font-medium">{Math.round(row.principal).toLocaleString("en-IN")}</td>
                      <td className="p-3 text-right text-gold">{Math.round(row.interest).toLocaleString("en-IN")}</td>
                      <td className="p-3 text-right text-slate-600">{Math.round(row.balance).toLocaleString("en-IN")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* Vehicle Loan EMI */}
      <section className="bg-white section-padding">
        <div className="container-custom max-w-4xl">
          <h2 className="text-3xl font-semibold text-navy mb-6 font-heading">Vehicle Loan EMI Calculator</h2>
          <p className="text-slate-600 mb-4">Planning to buy a two-wheeler, car, or commercial vehicle in Coimbatore? Vehicle loan EMI depends on several factors:</p>
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <h3 className="font-semibold text-navy mb-1">Two-Wheeler Loans</h3>
              <p className="text-slate-600 text-sm">EMIs start from as low as Rs. 1,500/month for entry-level bikes. Interest rates range from 8-14% with tenure of 1-5 years.</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <h3 className="font-semibold text-navy mb-1">Car Loans</h3>
              <p className="text-slate-600 text-sm">Car loan EMIs depend on the vehicle price, down payment (typically 10-20%), and tenure (up to 7 years). New car rates are lower than used car rates.</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <h3 className="font-semibold text-navy mb-1">Commercial Vehicle Loans</h3>
              <p className="text-slate-600 text-sm">For trucks, tempos, and fleet vehicles. EMI depends on vehicle cost, down payment, business income stability, and loan tenure (typically 3-5 years).</p>
            </div>
          </div>
          <p className="text-slate-600 mt-4 text-sm">Use the calculator above with the <strong>Vehicle Loan</strong> preset (8-14% interest) to estimate your EMI.</p>
        </div>
      </section>

      {/* Home Loan EMI */}
      <section className="bg-slate-50 section-padding">
        <div className="container-custom max-w-4xl">
          <h2 className="text-3xl font-semibold text-navy mb-6 font-heading">Home Loan EMI Calculator</h2>
          <p className="text-slate-600 mb-4">Home loans in Coimbatore typically have the longest tenure (up to 30 years), making EMI planning essential:</p>
          <div className="space-y-3">
            {[
              "Longer tenure reduces monthly EMI but increases total interest paid. A 20-year tenure vs 15-year can reduce EMI by 15-20%.",
              "Floating rate loans mean your EMI can change based on RBI repo rate adjustments. Most Coimbatore home buyers opt for floating rates as they trend lower over time.",
              "Prepayment significantly reduces total interest. Even paying one extra EMI per year can save lakhs over the loan period. RBI mandates zero prepayment charges for floating rate home loans.",
            ].map((text, i) => (
              <div key={i} className="flex gap-3 items-start p-3 bg-white rounded-lg border border-slate-200">
                <Calculator className="text-gold flex-shrink-0 mt-0.5" size={18} />
                <p className="text-slate-600 text-sm">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business & MSME Loan EMI */}
      <section className="bg-white section-padding">
        <div className="container-custom max-w-4xl">
          <h2 className="text-3xl font-semibold text-navy mb-6 font-heading">Business & MSME Loan EMI Calculation</h2>
          <p className="text-slate-600 mb-4">Business and MSME loan EMI in Coimbatore differs from personal loans due to several business-specific factors:</p>
          <div className="space-y-3">
            {[
              { title: "Turnover Impact", desc: "Your annual turnover determines the maximum loan amount you're eligible for, which directly affects EMI. Higher turnover = higher eligibility = potentially higher EMI." },
              { title: "Working Capital vs Term Loan", desc: "Term loans have fixed EMIs over the tenure. Working capital loans (CC/OD) have flexible repayment — you pay interest only on the utilized amount, making EMI variable." },
              { title: "CIBIL Score Matters", desc: "A CIBIL score of 750+ can get you 1-3% lower interest rates, significantly reducing your monthly EMI. For a Rs. 10 lakh loan, even 1% lower rate saves Rs. 500-600 per month." },
            ].map((item, i) => (
              <div key={i} className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-semibold text-navy mb-1">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Factors That Affect EMI */}
      <section className="bg-slate-50 section-padding">
        <div className="container-custom max-w-4xl">
          <h2 className="text-3xl font-semibold text-navy mb-6 font-heading">Factors That Affect EMI</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { title: "Loan Amount", desc: "Higher loan amount = higher EMI. Borrow only what you need." },
              { title: "Interest Rate", desc: "Even 0.5% difference significantly impacts total cost over long tenure." },
              { title: "Tenure", desc: "Longer tenure reduces EMI but increases total interest paid." },
              { title: "Credit Score", desc: "750+ CIBIL score helps negotiate lower rates from lenders." },
              { title: "Type of Lender", desc: "Banks offer lower rates than NBFCs, but NBFCs have easier eligibility." },
            ].map((item, i) => (
              <div key={i} className="flex gap-3 items-start p-4 bg-white rounded-lg border border-slate-200">
                <Calculator className="text-gold flex-shrink-0 mt-1" size={18} />
                <div>
                  <h3 className="font-semibold text-navy text-sm">{item.title}</h3>
                  <p className="text-slate-600 text-xs mt-1">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-semibold text-navy mb-4 font-heading">FAQs About EMI Calculation</h2>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="bg-slate-50 rounded-lg border border-slate-200 px-6" data-testid={`emi-faq-${i}`}>
                  <AccordionTrigger className="text-left text-navy font-medium py-4 hover:no-underline">{faq.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4">{faq.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy section-padding">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4 font-heading">Need Help Choosing the Right Loan?</h2>
          <p className="text-slate-300 mb-8 max-w-xl mx-auto">
            Not sure which loan suits you? Get professional loan assistance in Coimbatore. Our team helps you find the best rates, tenure, and lender for your needs.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/#contact" className="btn-primary flex items-center gap-2" data-testid="cta-contact-btn">
              Contact Us <ChevronRight size={18} />
            </Link>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn-whatsapp">
              <MessageCircle size={18} /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default EMICalculatorPage;
